// eslint-disable-next-line filenames/match-regex
import {useCallback, useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent} from 'react'

export interface FootnoteMethods {
  onFootnoteClick: (number: number, event: MouseEvent) => void
  onFootnoteEnter: (number: number, event: PointerEvent) => void
  onFootnoteKeyDown: (number: number, event: KeyboardEvent) => void
}

const useFootnotes = () => {
  const [footnoteStates, setFootnoteStates] = useState<Record<string, boolean>>({})
  const isClicking = useRef<boolean>(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const toggleFootnote = useCallback((number: number) => {
    setFootnoteStates(value => ({...value, [String(number)]: !value[String(number)]}))
  }, [])

  const openFootnote = useCallback(
    (number: number) => {
      if (footnoteStates[String(number)]) return
      setFootnoteStates(value => ({...value, [String(number)]: true}))
    },
    [footnoteStates],
  )

  const onFootnoteClick: FootnoteMethods['onFootnoteClick'] = useCallback(
    number => {
      if (isClicking.current) return
      isClicking.current = true

      timeoutRef.current = setTimeout(() => {
        isClicking.current = false
        timeoutRef.current = null
      }, 200)

      toggleFootnote(number)
    },
    [toggleFootnote],
  )

  const onFootnoteEnter: FootnoteMethods['onFootnoteEnter'] = useCallback(
    number => {
      if ('ontouchstart' in window || isClicking.current) return
      openFootnote(number)
    },
    [openFootnote],
  )

  const onFootnoteKeyDown: FootnoteMethods['onFootnoteKeyDown'] = useCallback(
    (number, event) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.key === 'Enter') {
        event.preventDefault()
        toggleFootnote(number)
      }
    },
    [toggleFootnote],
  )

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  return {footnoteStates, onFootnoteClick, onFootnoteEnter, onFootnoteKeyDown}
}

export default useFootnotes
